import { Badge } from "@shared/ui/badge";
import { Switch } from "@shared/ui/switch";
import { Edit, Trash2, Eye, Star } from "lucide-react";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import React, { useEffect, useState } from 'react';
import { Button } from "@shared/ui/button";
import { Label } from "@shared/ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@shared/ui/select";

interface Tip {
  id: string;
  title: string;
  quickSummary: string;
  description: string;
  utilityService: string;
  icon: string;
  isActive: boolean;
  order: number;
  helpfulCount: number;
}

interface TipsFilterPanelProps {
  currentFilters: {
    utilityService: string;
  };
  onApplyFilters: (filters: { utilityService?: string }) => void;
  onResetFilters: () => void;
  utilityServices: string[];
}

const TipsFilterPanel: React.FC<TipsFilterPanelProps> = ({
  currentFilters,
  onApplyFilters,
  onResetFilters,
  utilityServices,
}) => {
  const [tempFilters, setTempFilters] = useState({
    utilityService: currentFilters.utilityService || '',
  });

  useEffect(() => {
    setTempFilters({
      utilityService: currentFilters.utilityService || '',
    });
  }, [currentFilters]);

  const handleUtilityServiceChange = (value: string) => {
    setTempFilters((prev) => ({ ...prev, utilityService: value === 'ALL_SERVICES' ? '' : value }));
  };

  const handleApply = () => {
    const filtersToApply: { utilityService?: string } = {};
    
    if (tempFilters.utilityService) {
      filtersToApply.utilityService = tempFilters.utilityService;
    }
    
    onApplyFilters(filtersToApply);
  };

  const handleReset = () => {
    setTempFilters({ utilityService: '' });
    onResetFilters();
  };

  return (
    <form onSubmit={e => { e.preventDefault(); handleApply(); }}>
      <div className="grid grid-cols-1 gap-6">
        <div className="space-y-2">
          <Label>Utility Service</Label>
          <Select 
            value={tempFilters.utilityService || 'ALL_SERVICES'} 
            onValueChange={handleUtilityServiceChange}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select utility service" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="ALL_SERVICES">All Services</SelectItem>
                {utilityServices.map((service: string) => (
                  <SelectItem key={service} value={service}>
                    {service}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={handleReset}>
            Reset Filters
          </Button>
          <Button type="submit">
            Apply Filters
          </Button>
        </div>
      </div>
    </form>
  );
};

interface TipTableProps {
  tips: Tip[];
  availableIcons: { name: string; icon: any; value: string }[];
  onView: (tip: Tip) => void;
  onEdit: (tip: Tip) => void;
  onDelete: (id: string) => void;
  onToggleActive: (tip: Tip) => void;
  isUpdatingStatus?: boolean;
  // Search props
  searchPlaceholder?: string;
  searchParamName?: string;
  searchDebounceMs?: number;
  onSearch?: (searchTerm: string, paramName: "search_data") => void;
  // Filter props
  currentFilters?: {
    utilityService: string;
  };
  onApplyFilters?: (filters: { utilityService?: string }) => void;
  onResetFilters?: () => void;
  utilityServices?: string[];
  // Pagination props
  pagination?: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
    startItem: number;
    endItem: number;
  };
  onPageChange?: (newPage: number) => void;
}

export const TipTable = ({ 
  tips, 
  availableIcons, 
  onView, 
  onEdit, 
  onDelete, 
  onToggleActive, 
  isUpdatingStatus,
  searchPlaceholder,
  searchParamName,
  searchDebounceMs,
  onSearch,
  currentFilters,
  onApplyFilters,
  onResetFilters,
  utilityServices = [],
  pagination,
  onPageChange 
}: TipTableProps) => {
  const getIconComponent = (iconValue: string) => {
    const iconData = availableIcons.find(icon => icon.value === iconValue);
    return iconData ? iconData.icon : availableIcons[0].icon;
  };

  // Define table columns
  const columns: TableColumn<Tip>[] = [
    {
      key: "icon",
      header: "Icon",
      render: (value: string, tip: Tip) => {
        const IconComponent = getIconComponent(tip.icon);
        return <IconComponent className="h-4 w-4" />;
      },
      width: "w-16",
    },
    {
      key: "title",
      header: "Title",
      sortable: true,
      render: (value: string) => (
        <span className="font-medium">{value}</span>
      ),
    },
    {
      key: "quickSummary",
      header: "Quick Summary",
      sortable: true,
      render: (value: string) => (
        <span className="max-w-xs truncate block">{value}</span>
      ),
    },
    {
      key: "utilityService",
      header: "Utility Service",
      sortable: true,
      render: (value: string) => (
        <Badge variant="secondary">{value || 'No Service'}</Badge>
      ),
    },
    {
      key: "helpfulCount",
      header: "Helpful Count",
      sortable: true,
      render: (value: number) => (
        <div className="flex items-center gap-1">
          <Star className="h-3 w-3 text-yellow-500" />
          <span className="text-sm font-medium">{value}</span>
        </div>
      ),
      width: "w-32",
    },
    {
      key: "isActive",
      header: "Active",
      render: (value: boolean, tip: Tip) => (
        <Switch
          checked={tip.isActive}
          onCheckedChange={() => onToggleActive(tip)}
        />
      ),
      width: "w-20",
    },
  ];

  // Define table actions
  const actions = [
    {
      label: "View",
      icon: <Eye className="h-4 w-4" />,
      onClick: (tip: Tip) => onView(tip),
    },
    {
      label: "Edit",
      icon: <Edit className="h-4 w-4" />,
      onClick: (tip: Tip) => onEdit(tip),
    },
    {
      label: "Delete",
      icon: <Trash2 className="h-4 w-4" />,
      onClick: (tip: Tip) => onDelete(tip.id),
    },
  ];

  return (
    <GenericTable<Tip>
      data={tips}
      columns={columns}
      rowKey="id"
      actions={actions}
      actionsType="icons"
      searchPlaceholder={searchPlaceholder}
      searchParamName={searchParamName}
      searchDebounceMs={searchDebounceMs}
      onSearch={onSearch}
      filterPanel={
        currentFilters && onApplyFilters && onResetFilters ? (
          <TipsFilterPanel
            currentFilters={currentFilters}
            onApplyFilters={onApplyFilters}
            onResetFilters={onResetFilters}
            utilityServices={utilityServices}
          />
        ) : undefined
      }
      pagination={pagination}
      onPageChange={onPageChange}
      emptyMessage="No tips found"
    />
  );
};